/**
 * Subscription Chatbot component for trAInedup.ai
 *
 * This component renders a small chat widget that sits in the bottom right
 * corner of the screen on every page for visitors who are not authenticated.
 * It greets the visitor, asks them what they hope to achieve by using
 * trAInedup.ai and then, based on a simple keyword heuristic, recommends
 * one of the available subscription tiers.  The recommendations are
 * intentionally straightforward for now – if the visitor mentions a team or
 * company we assume they're best suited for the Corporate plan, if they
 * mention advanced or expert needs we suggest the Premium plan, otherwise
 * Standard is the default.  The widget can be minimised via the toggle
 * button or dismissed entirely using the close button in the header.
 */

import React, { useEffect, useState, FormEvent, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { TIER_FEATURES, SUBSCRIPTION_PRICES, ROUTES } from '../../utils/constants';
import { Link, useLocation } from 'react-router-dom';
import { subscriptionPlanAssistantService, PlanAssistantMessage } from '../../services/subscriptionPlanAssistant';
import { SUBSCRIPTION_TIERS } from '../../services/stripe/config';

/**
 * Represents a chat message in the conversation.  Each message has a sender
 * (either 'user' or 'bot') and some text content.  Additional properties
 * could be added in the future (e.g. timestamps or message IDs) if needed.
 */
interface ChatMessage {
  sender: 'user' | 'bot';
  text: string;
  planTier?: 'standard' | 'premium' | 'corporate';
}

/**
 * Generate a recommendation message for a given subscription tier.  This
 * helper formats a friendly response including the plan name, price and
 * highlights of what's included.  It also embeds a call‑to‑action link
 * inviting the visitor to sign up for the suggested tier via the register
 * page with a `plan` query parameter.  React Router's Link is used so the
 * navigation stays client‑side.
 *
 * @param tier The key for the tier returned by determinePlan
 * @returns A JSX element representing the assistant's reply
 */
const Recommendation: React.FC<{ tier: 'standard' | 'premium' | 'corporate' }> = ({ tier }) => {
  // Map internal keys to displayable names
  const displayNameMap: Record<string, string> = {
    standard: 'Standard',
    premium: 'Premium',
    corporate: 'Corporate',
  };
  const features = TIER_FEATURES[tier];
  const priceDisplay = tier === 'corporate' ? 'POA' : SUBSCRIPTION_PRICES[tier].display;
  const planName = displayNameMap[tier];
  // Construct a comma‑separated list of the first few features
  const featureList = features.slice(0, 3).join(', ') + (features.length > 3 ? ', …' : '');
  return (
    <span>
      Based on what you've shared, I recommend our <strong>{planName}</strong> plan ({priceDisplay}{tier !== 'corporate' ? '/month' : ''}). This plan includes {featureList}.{' '}
      {tier === 'corporate' ? (
        <Link to={'/corporate-request'} className="text-primary underline">
          Request a corporate package
        </Link>
      ) : (
        <>
          <Link to={`${ROUTES.register}?plan=${tier.toUpperCase()}`} className="text-primary underline">
            Click here
          </Link>{' '}
          to get started with {planName}.
        </>
      )}
    </span>
  );
};

/**
 * Helper function to format message text by converting URLs to clickable links
 * and preserving line breaks.
 */
const formatMessage = (text: string): string => {
  const baseText = DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  // Use a bounded, non-greedy regex to prevent ReDoS attacks
  // Matches URLs up to 2000 chars (reasonable URL length limit)
  const urlRegex = /(https?:\/\/[^\s]{1,2000}?)(?=\s|$|[.,;:!?)>\]}"'])/g;
  let formatted = baseText.replace(
    urlRegex,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="underline text-primary hover:text-primary-600">$1</a>'
  );
  formatted = formatted.replace(/\n/g, '<br>');

  return DOMPurify.sanitize(formatted, {
    ALLOWED_TAGS: ['a', 'br'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  });
};

// Create a custom event for opening the chatbot
export const OPEN_CHATBOT_EVENT = 'openChatbot';

// Session storage key for tracking manual closure
const CHATBOT_CLOSED_KEY = 'chatbot_manually_closed';

/**
 * The SubscriptionChatbot functional component.  It manages its own internal
 * state, including whether the widget is open or dismissed, the list of
 * chat messages and the current input value.  When mounted and opened for
 * the first time it posts an initial greeting.  It remains completely
 * hidden when a user is authenticated to avoid interfering with the
 * experience of paying customers.
 */
const SubscriptionChatbot: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { getCurrentTier } = useSubscription();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [autoOpenTriggered, setAutoOpenTriggered] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Check if current page is the registration or login page
  const isRegistrationPage = location.pathname === ROUTES.register;
  const isLoginPage = location.pathname === ROUTES.login;
  const isAuthPage = isRegistrationPage || isLoginPage;

  // Listen for custom event to open chatbot
  useEffect(() => {
    const handleOpenChatbot = () => {
      if (!isAuthPage) {
        setIsOpen(true);
      }
    };

    window.addEventListener(OPEN_CHATBOT_EVENT, handleOpenChatbot);

    return () => {
      window.removeEventListener(OPEN_CHATBOT_EVENT, handleOpenChatbot);
    };
  }, [isAuthPage]);

  // Auto-open chatbot after 90 seconds for non-authenticated users.
  useEffect(() => {
    // Clear any existing timer when effect re-runs
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Don't set up timer if authenticated or already triggered
    if (isAuthenticated || autoOpenTriggered) return;

    // Check if user has manually closed the chatbot in this session
    const wasManuallyClosed = sessionStorage.getItem(CHATBOT_CLOSED_KEY) === 'true';
    if (wasManuallyClosed) {
      return; // Don't auto-open if user has manually closed it
    }

    // Never auto-open on sign-in or registration pages.
    if (isAuthPage) {
      return;
    }

    // Start auto-open timer on all other applicable pages.
    timerRef.current = setTimeout(() => {
      setIsOpen(true);
      setAutoOpenTriggered(true);
    }, 90000); // 90 seconds

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isAuthenticated, autoOpenTriggered, isAuthPage]);

  // Initialise the conversation when the chatbot is opened for the first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          sender: 'bot',
          text: 'Hi there! 👋 What brings you to trAInedup.ai and how do you hope AI could help you?',
        },
      ]);
    }
  }, [isOpen, messages.length]);

  // Hide the widget for authenticated users with paid subscriptions
  // Show for: unauthenticated visitors OR free tier users
  if (isAuthPage) {
    return null;
  }

  if (isAuthenticated) {
    const currentTier = getCurrentTier();
    // Only show for free tier users
    if (currentTier !== SUBSCRIPTION_TIERS.FREE) {
      return null;
    }
  }

  /**
   * Handle sending of a message when the form is submitted.  The user's
   * message is added to the conversation, then a recommendation is computed
   * and appended as the assistant's reply.  The input field is cleared
   * afterwards.
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;

    setMessages((prev) => [...prev, { sender: 'user', text: trimmed }]);
    setInputValue('');
    setIsLoading(true);

    try {
      const history: PlanAssistantMessage[] = messages.map((m) => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text,
      }));
      const result = await subscriptionPlanAssistantService.send(trimmed, history);
      const assistantReply = result.response;
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: assistantReply,
          planTier: result.planTier,
        },
      ]);
    } catch (err) {
      console.error('[SubscriptionChatbot] subscriptionPlanAssistant error', err);
      setMessages((prev) => [
        ...prev,
        { sender: 'bot', text: 'Sorry, I had a hiccup there. Please try again or rephrase your last message.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle the open/closed state of the chat window.
   * When user manually opens it, clear the "manually closed" flag.
   */
  const toggleChat = () => {
    setIsOpen((prev) => {
      const newState = !prev;
      // If opening the chat, clear the manually closed flag
      if (newState) {
        sessionStorage.removeItem(CHATBOT_CLOSED_KEY);
      }
      return newState;
    });
  };

  /**
   * Close the chat window and mark it as manually closed.
   * This prevents auto-reopening during the same session.
   * The icon will remain visible so users can reopen it manually.
   */
  const handleDismiss = () => {
    setIsOpen(false);
    // Mark as manually closed in session storage
    sessionStorage.setItem(CHATBOT_CLOSED_KEY, 'true');
  };

  return (
    <div>
      {/* Toggle button */}
      <button
        type="button"
        className="fixed bottom-4 right-4 w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-transform transform hover:scale-105 focus:outline-none z-[9999]"
        onClick={toggleChat}
      >
        {isOpen ? (
          // Close icon
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          // Chat icon
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m-4 8h6a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v10l-4 4 4-4" />
          </svg>
        )}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 w-80 md:w-96 bg-white shadow-xl border border-neutral-200 rounded-lg flex flex-col z-50">
          {/* Header */}
          <div className="flex items-center justify-between bg-primary text-white px-4 py-3 rounded-t-lg">
            <span className="font-semibold">Your AI Assistant</span>
            <button onClick={handleDismiss} className="text-white hover:text-neutral-200 focus:outline-none">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Messages container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '300px' }}>
            {messages.map((msg, index) => {
              const isUser = msg.sender === 'user';
              return (
                <div key={index} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-xs px-3 py-2 rounded-lg text-sm leading-snug whitespace-pre-wrap ${
                      isUser
                        ? 'bg-primary text-white rounded-br-none'
                        : 'bg-neutral-200 text-neutral-800 rounded-bl-none'
                    }`}
                  >
                    {msg.planTier ? (
                      <Recommendation tier={msg.planTier} />
                    ) : (
                      <span dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }} />
                    )}
                  </div>
                </div>
              );
            })}
            {isLoading && (
              <div className="flex justify-start">
                <div className="max-w-xs px-3 py-2 rounded-lg text-sm leading-snug bg-neutral-200 text-neutral-600 rounded-bl-none animate-pulse">
                  Thinking...
                </div>
              </div>
            )}
          </div>
          {/* Input area */}
          <form onSubmit={handleSubmit} className="border-t border-neutral-200 p-2 flex">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="flex-grow px-3 py-2 text-sm border border-neutral-300 rounded-l-lg focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Type your answer..."
            />
            <button
              type="submit"
              disabled={isLoading}
              className="bg-primary text-white px-4 py-2 rounded-r-lg text-sm font-medium hover:bg-primary-600 focus:outline-none disabled:opacity-50"
            >
              {isLoading ? '...' : 'Send'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default SubscriptionChatbot;
